// Steps/VolumeStep.tsx
import React from "react";
import { Field, useFormikContext } from "formik";
import { Form } from "react-bootstrap";
import Select from "react-select";

const DOCUMENT_VOLUMES_ONETIME = [
  { value: "1-50", label: "1-50 documents" },
  { value: "51-200", label: "51-200 documents" },
  { value: "201-500", label: "201-500 documents" },
  { value: "501-1000", label: "501-1,000 documents" },
  { value: "1000+", label: "1,000+ documents" },
];

const DOCUMENT_VOLUMES_MONTHLY = [
  { value: "1-50", label: "1-50 documents per month" },
  { value: "51-200", label: "51-200 documents per month" },
  { value: "201-500", label: "201-500 documents per month" },
  { value: "501-1000", label: "501-1,000 documents per month" },
  { value: "1000+", label: "1,000+ documents per month" },
];

const USE_CASE_TYPES = [
  {
    value: "one-off",
    title: "One-Time Project",
    description: "A specific batch of documents I need to process once",
    icon: "📄",
  },
  {
    value: "ongoing",
    title: "Ongoing Work",
    description: "Regular document processing as part of my day-to-day",
    icon: "🔄",
  },
];

export const VolumeStep: React.FC = () => {
  const { values } = useFormikContext<any>();

  return (
    <div className="py-2">
      <Form.Group className="mb-2">
        <Form.Label className="h5 mb-3">
          Is this a one-time project or ongoing work?
        </Form.Label>
        <Field name="use_case_type">
          {({ field, form: { touched, errors, setFieldValue } }) => {
            const select = (value: string) => {
              setFieldValue(field.name, value);
              setFieldValue("document_volume", "");
              if (value === "one-off") {
                setFieldValue("document_frequency", "one-time");
              } else if (value === "ongoing") {
                setFieldValue("document_frequency", "recurring");
              }
            };
            return (
              <>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(200px, 1fr))",
                    gap: "0.75rem",
                  }}
                >
                  {USE_CASE_TYPES.map((useCase) => {
                    const isSelected = field.value === useCase.value;
                    return (
                      <button
                        type="button"
                        key={useCase.value}
                        onClick={() => select(useCase.value)}
                        style={{
                          border: isSelected
                            ? "2px solid #3f6ad8"
                            : "2px solid #dee2e6",
                          borderRadius: "12px",
                          background: isSelected ? "#eef2fc" : "#fff",
                          padding: "0.875rem 0.875rem",
                          textAlign: "left",
                          cursor: "pointer",
                          transition:
                            "border-color 0.15s, background 0.15s",
                          outline: "none",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "1.5rem",
                            marginBottom: "0.25rem",
                          }}
                        >
                          {useCase.icon}
                        </div>
                        <div
                          style={{
                            fontWeight: 600,
                            marginBottom: "0.15rem",
                            fontSize: "0.95rem",
                          }}
                        >
                          {useCase.title}
                        </div>
                        <div
                          style={{
                            color: "#6c757d",
                            fontSize: "0.8rem",
                            lineHeight: 1.35,
                          }}
                        >
                          {useCase.description}
                        </div>
                      </button>
                    );
                  })}
                </div>
                {touched.use_case_type &&
                  errors.use_case_type &&
                  !field.value && (
                    <div className="invalid-feedback d-block">
                      {errors.use_case_type}
                    </div>
                  )}
              </>
            );
          }}
        </Field>
      </Form.Group>

      {/* Show volume selector for one-time projects - DISABLED */}
      {false && values.use_case_type === "one-off" && (
        <Form.Group className="mb-4">
          <Form.Label className="h5">
            How many documents do you need to process?
          </Form.Label>
          <p className="text-muted mb-3">
            This helps us recommend the right pricing option
          </p>
          <Field name="document_volume">
            {({ field, form: { touched, errors, setFieldValue } }) => (
              <>
                <Select
                  options={DOCUMENT_VOLUMES_ONETIME}
                  value={DOCUMENT_VOLUMES_ONETIME.find(
                    (option) => option.value === field.value
                  )}
                  onChange={(option) =>
                    setFieldValue(field.name, option?.value)
                  }
                  isClearable
                  placeholder="Select document volume..."
                  className={`form-select-container ${
                    touched.document_volume && errors.document_volume
                      ? "is-invalid"
                      : ""
                  }`}
                  classNamePrefix="form-select"
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderColor:
                        touched.document_volume && errors.document_volume
                          ? "#dc3545"
                          : base.borderColor,
                      padding: "4px",
                      fontSize: "16px",
                    }),
                  }}
                />
                {touched.document_volume && errors.document_volume && (
                  <div className="invalid-feedback d-block">
                    {errors.document_volume}
                  </div>
                )}
              </>
            )}
          </Field>
        </Form.Group>
      )}

      {/* Show volume selector for ongoing needs - DISABLED */}
      {false && values.use_case_type === "ongoing" && (
        <Form.Group className="mb-4">
          <Form.Label className="h5">
            What's your typical monthly volume?
          </Form.Label>
          <p className="text-muted mb-3">
            This helps us recommend the right plan tier
          </p>
          <Field name="document_volume">
            {({ field, form: { touched, errors, setFieldValue } }) => (
              <>
                <Select
                  options={DOCUMENT_VOLUMES_MONTHLY}
                  value={DOCUMENT_VOLUMES_MONTHLY.find(
                    (option) => option.value === field.value
                  )}
                  onChange={(option) =>
                    setFieldValue(field.name, option?.value)
                  }
                  isClearable
                  placeholder="Select typical monthly volume..."
                  className={`form-select-container ${
                    touched.document_volume && errors.document_volume
                      ? "is-invalid"
                      : ""
                  }`}
                  classNamePrefix="form-select"
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderColor:
                        touched.document_volume && errors.document_volume
                          ? "#dc3545"
                          : base.borderColor,
                      padding: "4px",
                      fontSize: "16px",
                    }),
                  }}
                />
                {touched.document_volume && errors.document_volume && (
                  <div className="invalid-feedback d-block">
                    {errors.document_volume}
                  </div>
                )}
              </>
            )}
          </Field>
        </Form.Group>
      )}
    </div>
  );
};
